/* ==========================================================================
   ROFT Landingpage Script - Interactions, Countdown, Glow Tracking, Modals
   ========================================================================== */

import { CONTACT_CONFIG } from './contact.js';

document.addEventListener('DOMContentLoaded', () => {

  // 1. Navbar Scroll Effect
  const navbar = document.getElementById('navbar');
  window.addEventListener('scroll', () => {
    if (window.scrollY > 50) {
      navbar.classList.add('scrolled');
    } else {
      navbar.classList.remove('scrolled');
    }
  });

  // 2. Mobile navigation
  const mobileToggle = document.getElementById('mobile-toggle');
  const mainNavigation = document.getElementById('main-navigation');
  if (mobileToggle && mainNavigation) {
    mobileToggle.addEventListener('click', () => {
      const isOpen = mainNavigation.classList.toggle('is-open');
      mobileToggle.setAttribute('aria-expanded', String(isOpen));
      mobileToggle.setAttribute('aria-label', isOpen ? 'Menü schließen' : 'Menü öffnen');
    });

    mainNavigation.querySelectorAll('a').forEach(link => {
      link.addEventListener('click', () => {
        mainNavigation.classList.remove('is-open');
        mobileToggle.setAttribute('aria-expanded', 'false');
        mobileToggle.setAttribute('aria-label', 'Menü öffnen');
      });
    });
  }

  // 3. Legacy status container guard
  const betaStatusDate = new Date('2026-06-26T00:00:00').getTime();

  function updateCountdown() {
    const countdownGrid = document.querySelector('.countdown-grid');
    if (!countdownGrid) return;

    const now = new Date().getTime();
    const difference = betaStatusDate - now;

    if (difference < 0) {
      countdownGrid.innerHTML = `
        <div style="font-family: 'Outfit', sans-serif; font-size: 24px; font-weight: 800; color: #00F0FF; padding: 20px; text-shadow: 0 0 15px rgba(0, 240, 255, 0.4);">
          ROFT WIRD AKTUELL GETESTET.
        </div>
      `;
      return;
    }

    const days = Math.floor(difference / (1000 * 60 * 60 * 24));
    const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((difference % (1000 * 60)) / 1000);

    document.getElementById('cd-days').textContent = days.toString().padStart(2, '0');
    document.getElementById('cd-hours').textContent = hours.toString().padStart(2, '0');
    document.getElementById('cd-minutes').textContent = minutes.toString().padStart(2, '0');
    document.getElementById('cd-seconds').textContent = seconds.toString().padStart(2, '0');
  }

  updateCountdown();
  setInterval(updateCountdown, 1000);

  // 4. Scroll-Triggered Fade-In Animations (Intersection Observer)
  const animatableElements = document.querySelectorAll('.animate-on-scroll');
  
  const scrollObserver = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('active');
        scrollObserver.unobserve(entry.target);
      }
    });
  }, {
    threshold: 0.1,
    rootMargin: '0px 0px -50px 0px'
  });

  animatableElements.forEach(element => {
    scrollObserver.observe(element);
  });

  // 5. Ambient Cursor Glow Tracking (Only for Desktop viewports to save mobile performance)
  const glowCyan = document.querySelector('.glow-orb-cyan');
  const glowPink = document.querySelector('.glow-orb-pink');

  if (window.innerWidth > 1024 && glowCyan && glowPink) {
    window.addEventListener('mousemove', (e) => {
      const mouseX = e.clientX;
      const mouseY = e.clientY;
      
      glowCyan.style.transform = `translate(${mouseX * 0.05}px, ${mouseY * 0.05}px)`;
      glowPink.style.transform = `translate(${-mouseX * 0.03}px, -${mouseY * 0.03}px)`;
    });
  }

  // ==========================================================================
  // 6. Interactive App Tour (Swapping data-tabs & replicating screenshot details)
  // ==========================================================================
  const tourTabs = document.querySelectorAll('.tour-tab-btn');
  const tourInfoBox = document.getElementById('tour-info-box');
  const phoneScreenDisplay = document.getElementById('phone-screen-display');
  const phoneHeaderTitle = document.getElementById('phone-header-title');

  // Copy structures matching the real screenshots exactly
  const tourContentData = {
    'tab-home': {
      index: '1 / 5',
      headline: 'Startseite & Live-Ticker',
      lead: 'Deine Zentrale für die Straße.',
      desc: 'Erhalte sofortigen Zugriff auf die neuesten Treffen und regionalen Hotspots. Sieh in Sekundenschnelle im Live-Ticker, was gerade in deiner Umgebung passiert – ob spontane Treffen, Kontrollen oder Event-Updates. Du entscheidest, was wichtig ist.',
      submockup: `
        <div class="role-mockup-card" style="border-color:var(--color-cyan); background:rgba(0, 240, 255, 0.02)">
          <p><span class="pulse-indicator"></span> Live-Feed & Ticker Aktiv</p>
          <div style="font-size:11px; color:var(--color-text-secondary); line-height:1.5;">
            Melde Vorfälle, plane spontane Ausfahrten oder teile Neuigkeiten direkt mit deiner Community in Echtzeit.
          </div>
        </div>
      `
    },
    'tab-qahof': {
      index: '2 / 5',
      headline: 'Q&A & Hall of Fame',
      lead: 'Hol dir Rat und hol dir Ruhm.',
      desc: 'Das Herzstück für Tuning-Enthusiasten. Stell technische Fragen im Q&A-Forum oder präsentiere dein fertiges Projekt in der Hall of Fame. Bewerte andere Setups mit dem schnellen Swipe-Voting-System. Eine Flamme steht dabei für doppelte Likes!',
      submockup: `
        <div class="role-mockup-card" style="border-color:var(--color-cyan)">
          <p>🔥 Doppelter Vote durch Gold-Flames:</p>
          <div style="font-size:11px; color:var(--color-text-secondary); line-height:1.5; margin-bottom:8px;">
            Verteile normale Herzen oder nutze deine wertvollen täglichen Flammen-Likes, um deinen Lieblings-Builds an die Spitze der wöchentlichen Rangliste zu verhelfen.
          </div>
        </div>
      `
    },
    'tab-community': {
      index: '3 / 5',
      headline: 'Community & Treffen',
      lead: 'Verbinde dich mit Gleichgesinnten.',
      desc: 'Finde Tuning-Begeisterte in deiner Region über die interaktive Map. Organisiere oder nimm an offiziellen sowie spontanen Meets & Ausfahrten teil. Tausche dich über sichere, direkte In-App-Chats aus und netzwerke mit echten Benzinköpfen.',
      submockup: `
        <div class="role-mockup-card">
          <p>💬 Direkte Echtzeit-Chats:</p>
          <div style="font-size:11px; color:var(--color-text-secondary); line-height:1.5;">
            Dank des integrierten Online-Status siehst du sofort, wer gerade in deiner Nähe aktiv ist und Lust auf ein spontanes Treffen oder eine Ausfahrt hat.
          </div>
        </div>
      `
    },
    'tab-market': {
      index: '4 / 5',
      headline: 'Automotive Marketplace',
      lead: 'Kaufe und verkaufe unter Kennern.',
      desc: 'Der spezialisierte Community-Marktplatz direkt in der App. Finde Performance-Teile, Felgensätze oder dein nächstes Fahrzeug. Sende eine Anfrage und kläre Kauf und Versand im Chat mit der anbietenden Person.',
      submockup: `
        <div class="role-mockup-card" style="border-color:var(--color-cyan)">
          <p>🛒 Inserat-Upload-Workflow:</p>
          <div style="font-size:11px; color:var(--color-text-secondary); line-height:1.5; margin-bottom:8px;">
            Erstelle Inserate in unter 2 Minuten mit unserem cleveren Upload-Assistenten. Jedes Angebot wird von Admins auf Echtheit geprüft.
          </div>
        </div>
      `
    },
    'tab-profile': {
      index: '5 / 5',
      headline: 'Fahrzeugprofil & Garage',
      lead: 'Deine digitale Garage mit echtem Wert.',
      desc: 'Dein virtueller Showroom für all deine Fahrzeuge. Lade Spezifikationen, Leistungskurven, Modifikationslisten und Soundfiles hoch. Jedes Auto oder Bike in deiner Garage wird von ROFT verifiziert, um Fake-Profile absolut auszuschließen.',
      submockup: `
        <div class="role-mockup-card" style="border-color:var(--color-cyan)">
          <p>💎 In-App Wallet & Credits:</p>
          <div style="font-size:11px; color:var(--color-text-secondary); line-height:1.5; margin-bottom:8px;">
            Sammle Credits durch Community-Aktivitäten oder lade sie auf, um deine Inserate zu boosten oder exklusive digitale HOF-Features freizuschalten.
          </div>
          <button class="phone-credits-btn" id="trigger-wallet-overlay">Wallet öffnen (Demo)</button>
        </div>
      `
    }
  };

  function renderTourTab(tabId) {
    const data = tourContentData[tabId];
    if (!data) return;

    // 1. Update Left Info Box with fade transition
    tourInfoBox.style.opacity = '0';
    tourInfoBox.style.transform = 'translateY(15px)';
    
    setTimeout(() => {
      tourInfoBox.innerHTML = `
        <span class="tour-index-badge">${data.index}</span>
        <h3>${data.headline}</h3>
        <p class="tour-lead">${data.lead}</p>
        <p class="tour-desc">${data.desc}</p>
        <div class="tour-sub-mockups">
          ${data.submockup}
        </div>
      `;
      tourInfoBox.style.opacity = '1';
      tourInfoBox.style.transform = 'translateY(0)';
      
      // Wire dynamic sub-mockup buttons
      document.getElementById('trigger-wallet-overlay')?.addEventListener('click', () => {
        const popup = document.getElementById('wallet-popup-mock');
        if (popup) popup.style.display = popup.style.display === 'none' ? 'block' : 'none';
      });
    }, 250);

    // 2. Switch phone screen mockup
    const allScreens = document.querySelectorAll('.phone-screen-content');
    allScreens.forEach(screen => screen.classList.remove('active'));

    const phoneScreen = document.getElementById('phone-screen-display');

    // Update phone headers according to the active tab
    if (tabId === 'tab-home') {
      document.getElementById('m-home').classList.add('active');
      phoneHeaderTitle.textContent = 'Roft';
      setPhoneTabActive('ph-tabbar-home');
      phoneScreen?.classList.add('screenshot-mode');
      
      // Reset scroll position on home tab click
      const viewport = document.getElementById('phone-home-viewport');
      if (viewport) {
        viewport.scrollTop = 0;
      }
    } else {
      phoneScreen?.classList.remove('screenshot-mode');
      if (tabId === 'tab-qahof') {
        document.getElementById('m-hof').classList.add('active');
        phoneHeaderTitle.textContent = 'Showroom';
        setPhoneTabActive('ph-tabbar-qa');
      } else if (tabId === 'tab-community') {
        document.getElementById('m-chat').classList.add('active');
        phoneHeaderTitle.textContent = 'Community';
        setPhoneTabActive('ph-tabbar-community');
      } else if (tabId === 'tab-market') {
        document.getElementById('m-market').classList.add('active');
        phoneHeaderTitle.textContent = 'Markt';
        setPhoneTabActive('ph-tabbar-market');
      } else if (tabId === 'tab-profile') {
        document.getElementById('m-garage').classList.add('active');
        phoneHeaderTitle.textContent = 'Profil';
        setPhoneTabActive('ph-tabbar-profile');
      }
    }
  }

  function setPhoneTabActive(tabElementId) {
    const tabIcons = document.querySelectorAll('.phone-tab-icon');
    tabIcons.forEach(icon => icon.classList.remove('active'));
    document.getElementById(tabElementId)?.classList.add('active');
  }

  // Wire Tab button click listeners
  tourTabs.forEach(btn => {
    btn.addEventListener('click', (e) => {
      const tabId = btn.getAttribute('data-tab');
      
      // Update active nav button styling
      tourTabs.forEach(t => t.classList.remove('active'));
      btn.classList.add('active');

      renderTourTab(tabId);
    });
  });

  // Wire Phone simulator tabbar icons click listeners
  const phoneTabbarIcons = document.querySelectorAll('.phone-tab-icon');
  phoneTabbarIcons.forEach(icon => {
    icon.style.pointerEvents = 'auto'; // ensure click events flow through even if transparent
    icon.addEventListener('click', (e) => {
      e.stopPropagation();
      const id = icon.id;
      let targetTourTab = 'tab-home';
      if (id === 'ph-tabbar-home') targetTourTab = 'tab-home';
      else if (id === 'ph-tabbar-qa') targetTourTab = 'tab-qahof';
      else if (id === 'ph-tabbar-community') targetTourTab = 'tab-community';
      else if (id === 'ph-tabbar-market') targetTourTab = 'tab-market';
      else if (id === 'ph-tabbar-profile') targetTourTab = 'tab-profile';
      
      // Update active nav button styling in main tour tabs
      const correspondingBtn = document.querySelector(`.tour-tab-btn[data-tab="${targetTourTab}"]`);
      if (correspondingBtn) {
        tourTabs.forEach(t => t.classList.remove('active'));
        correspondingBtn.classList.add('active');
      }
      
      renderTourTab(targetTourTab);
    });
  });

  // Wire Phone simulator map FAB click listener
  const phoneMapFab = document.getElementById('phone-map-fab');
  phoneMapFab?.addEventListener('click', (e) => {
    e.stopPropagation();
    
    // Switch to Community tab in tour navigation
    const communityBtn = document.querySelector('.tour-tab-btn[data-tab="tab-community"]');
    if (communityBtn) {
      tourTabs.forEach(t => t.classList.remove('active'));
      communityBtn.classList.add('active');
    }
    
    renderTourTab('tab-community');
  });

  // Render initial tab on boot
  renderTourTab('tab-home');

  // ==========================================================================
  // 7. Mini In-App Micro Interactions inside phone mockups
  // ==========================================================================

  // Stepper inside Marketplace popup card (Image 1)
  const mktPlusBtn = document.getElementById('mkt-plus-btn');
  const mktStepperPopup = document.getElementById('mkt-stepper-popup');
  const mktStepperClose = document.getElementById('mkt-stepper-close');

  mktPlusBtn?.addEventListener('click', () => {
    if (mktStepperPopup) mktStepperPopup.style.display = 'block';
  });

  mktStepperClose?.addEventListener('click', () => {
    if (mktStepperPopup) mktStepperPopup.style.display = 'none';
  });

  // Discover/Detailed item detail tab switcher inside phone screen (Image 2)
  const phTabMktDiscover = document.getElementById('ph-tab-mkt-discover');
  const phTabMktDetail = document.getElementById('ph-tab-mkt-detail');
  const phSubMktDiscover = document.getElementById('ph-sub-mkt-discover');
  const phSubMktDetail = document.getElementById('ph-sub-mkt-detail');

  phTabMktDiscover?.addEventListener('click', () => {
    phTabMktDiscover.classList.add('active');
    phTabMktDetail?.classList.remove('active');
    if (phSubMktDiscover) phSubMktDiscover.style.display = 'block';
    if (phSubMktDetail) phSubMktDetail.style.display = 'none';
  });

  phTabMktDetail?.addEventListener('click', () => {
    phTabMktDetail.classList.add('active');
    phTabMktDiscover?.classList.remove('active');
    if (phSubMktDiscover) phSubMktDiscover.style.display = 'none';
    if (phSubMktDetail) phSubMktDetail.style.display = 'block';
  });

  // Chat threads and dialogues triggers (Image 1 Chats)
  const chatThreadSophie = document.getElementById('chat-thread-sophie');
  const chatDialoguePopup = document.getElementById('chat-dialogue-popup');
  const chatDialogueClose = document.getElementById('chat-dialogue-close');

  chatThreadSophie?.addEventListener('click', () => {
    if (chatDialoguePopup) chatDialoguePopup.style.display = 'block';
  });

  chatDialogueClose?.addEventListener('click', () => {
    if (chatDialoguePopup) chatDialoguePopup.style.display = 'none';
  });

  // Hof Swipe Cards & Leaderboard switcher (Image 2 & 3 Hof)
  const phTabHofList = document.getElementById('ph-tab-hof-list');
  const phTabHofRate = document.getElementById('ph-tab-hof-rate');
  const phSubList = document.getElementById('ph-sub-list');
  const phSubRate = document.getElementById('ph-sub-rate');

  phTabHofList?.addEventListener('click', () => {
    phTabHofList.classList.add('active');
    phTabHofRate?.classList.remove('active');
    if (phSubList) phSubList.style.display = 'block';
    if (phSubRate) phSubRate.style.display = 'none';
  });

  phTabHofRate?.addEventListener('click', () => {
    phTabHofRate.classList.add('active');
    phTabHofList?.classList.remove('active');
    if (phSubList) phSubList.style.display = 'none';
    if (phSubRate) phSubRate.style.display = 'block';
  });

  // Swiping rating action buttons simulation (Hof swipe)
  const swipeButtons = document.querySelectorAll('.phone-rate-btn');
  swipeButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const card = btn.closest('.phone-rate-card');
      if (!card) return;

      // Animate swipe translation
      card.style.transition = 'transform 0.4s ease, opacity 0.4s ease';
      if (btn.classList.contains('phone-rate-btn-close')) {
        card.style.transform = 'translateX(-120px) rotate(-15deg)';
      } else {
        card.style.transform = 'translateX(120px) rotate(15deg)';
      }
      card.style.opacity = '0';

      // Respawn card after simulation
      setTimeout(() => {
        card.style.transition = 'none';
        card.style.transform = 'none';
        card.style.opacity = '1';
      }, 800);
    });
  });


  // ==========================================================================
  // 9. Legal Modals Controller (Impressum, Datenschutz, Kontakt)
  // ==========================================================================
  const legalModal = document.getElementById('legal-modal');
  const modalBackdrop = document.getElementById('modal-backdrop');
  const modalClose = document.getElementById('modal-close');
  const modalTitle = document.getElementById('modal-title');
  const modalContent = document.getElementById('modal-content');

  const legalContent = {
    impressum: `
      <strong>Betreiber der Webseite:</strong><br>
      ROFT Automotive GmbH i.G.<br>
      Tuning-Allee 46<br>
      10115 Berlin<br>
      Deutschland<br><br>
      
      <strong>Vertreten durch:</strong><br>
      Yunus (CEO & Founder)<br><br>
      
      <strong>Kontakt:</strong><br>
      E-Mail: ${CONTACT_CONFIG.supportEmail}<br>
      Telefon: +49 (0) 30 12345678<br><br>
      
      <strong>Registereintrag:</strong><br>
      Registergericht: Amtsgericht Charlottenburg (Berlin)<br>
      Registernummer: HRB 202646<br><br>
      
      <strong>Haftungsausschluss:</strong><br>
      Trotz sorgfältiger inhaltlicher Kontrolle übernehmen wir keine Haftung für die Inhalte externer Links. Für den Inhalt der verlinkten Seiten sind ausschließlich deren Betreiber verantwortlich.
    `,
    datenschutz: `
      <strong>1. Datenschutz auf einen Blick</strong><br>
      Wir freuen uns über dein Interesse an der ROFT App. Der Schutz deiner persönlichen Daten ist uns ein wichtiges Anliegen. Nachfolgend informieren wir dich über die Verarbeitung deiner Daten.<br><br>
      
      <strong>2. Datenerfassung auf dieser Webseite</strong><br>
      Auf dieser Website gibt es kein Eingabeformular für E-Mail-Updates. Informationen zur Verarbeitung personenbezogener Daten in der ROFT App findest du in der verlinkten Datenschutzerklärung.<br><br>
      
      <strong>3. Deine Rechte</strong><br>
      Du hast jederzeit das Recht auf unentgeltliche Auskunft über Herkunft, Empfänger und Zweck deiner gespeicherten personenbezogenen Daten. Du hast außerdem ein Recht, die Berichtigung, Sperrung oder Löschung dieser Daten zu verlangen. Sende dazu einfach eine E-Mail an ${CONTACT_CONFIG.privacyEmail}.
    `,
    kontakt: `
      <strong>Kontaktiere das ROFT-Entwicklerteam:</strong><br>
      Hast du Fragen, Feedback oder möchtest als Partner/Tuning-Marke bei den exklusiven Szene-Deals dabei sein? Schreibe uns direkt!<br><br>
      
      <strong>E-Mail:</strong><br>
      <a href="mailto:${CONTACT_CONFIG.supportEmail}" style="color:var(--color-cyan); text-decoration:none;">${CONTACT_CONFIG.supportEmail}</a><br><br>
      
      <strong>Social Media Direct Messages:</strong><br>
      Instagram: <a href="https://www.instagram.com/ROFT_Official/" target="_blank" style="color:var(--color-cyan); text-decoration:none;">@ROFT_Official</a><br>
      TikTok: <a href="https://www.tiktok.com/@ROFT_Official" target="_blank" style="color:var(--color-cyan); text-decoration:none;">@ROFT_Official</a><br><br>
      
      Wir bemühen uns, alle Anfragen innerhalb von 24 Stunden zu beantworten. Ride safe!
    `
  };

  function openModal(type) {
    if (!legalModal || !modalBackdrop) return;
    modalTitle.textContent = type.charAt(0).toUpperCase() + type.slice(1);
    modalContent.innerHTML = legalContent[type];
    legalModal.style.display = 'block';
    modalBackdrop.style.display = 'block';
    document.body.style.overflow = 'hidden';
  }

  function closeModal() {
    if (!legalModal || !modalBackdrop) return;
    legalModal.style.display = 'none';
    modalBackdrop.style.display = 'none';
    document.body.style.overflow = '';
  }

  document.getElementById('link-impressum')?.addEventListener('click', (e) => { e.preventDefault(); openModal('impressum'); });
  
  modalClose?.addEventListener('click', closeModal);
  modalBackdrop?.addEventListener('click', closeModal);

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeModal();
  });

  // ==========================================================================
  // 10. Client-Side Chroma Keying (Dynamically strips out any off-black background)
  // ==========================================================================
  const heroEmblem = document.querySelector('.hero-phone-emblem');
  if (heroEmblem) {
    const handleChromaKey = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = heroEmblem.naturalWidth;
        canvas.height = heroEmblem.naturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(heroEmblem, 0, 0);
        
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;
        
        // Loop through pixels and turn any off-black/dark background transparent
        for (let i = 0; i < data.length; i += 4) {
          const r = data[i];
          const g = data[i+1];
          const b = data[i+2];
          
          if (r < 18 && g < 18 && b < 18) {
            data[i+3] = 0; // Alpha = 0 (Transparent)
          }
        }
        
        ctx.putImageData(imgData, 0, 0);
        heroEmblem.src = canvas.toDataURL();
        heroEmblem.style.mixBlendMode = 'normal'; // Reset blend mode since background is physically transparent
      } catch (err) {
        console.warn("Chroma keying skipped:", err);
      }
    };

    if (heroEmblem.complete) {
      handleChromaKey();
    } else {
      heroEmblem.addEventListener('load', handleChromaKey);
    }
  }

  // ==========================================================================
  // 11. Interactive State Scanner (Map Console overlay hover updates)
  // ==========================================================================
  const mapStates = document.querySelectorAll('.map-state');
  const mapOverlay = document.querySelector('.map-overlay');
  
  if (mapStates.length > 0 && mapOverlay) {
    const originalOverlayHtml = mapOverlay.innerHTML;
    const statesArray = Array.from(mapStates);
    
    mapStates.forEach(state => {
      state.addEventListener('mouseenter', () => {
        const name = state.getAttribute('data-name');
        const profiles = state.getAttribute('data-profiles');
        const isEven = statesArray.indexOf(state) % 2 === 0;
        const color = isEven ? 'var(--color-pink)' : 'var(--color-cyan)';
        
        mapOverlay.style.borderColor = color;
        mapOverlay.style.boxShadow = `0 0 15px ${color}1a`;
        mapOverlay.innerHTML = `
          <span class="pulse-indicator" style="background:${color}; box-shadow: 0 0 10px ${color};"></span>
          Region: <strong style="color:#fff; font-weight:800; margin-left:2px;">${name}</strong> 
          <span style="color:var(--color-text-muted); margin:0 4px;">|</span> 
          <strong style="color:${color};">${profiles} Profile aktiv</strong>
        `;
      });
      
      state.addEventListener('mouseleave', () => {
        mapOverlay.style.borderColor = 'rgba(255,255,255,0.1)';
        mapOverlay.style.boxShadow = 'none';
        mapOverlay.innerHTML = originalOverlayHtml;
      });
    });
  }

  // ==========================================================================
  // 12. Dynamic Live Statistics & HOF Loader
  // ==========================================================================
  async function loadLiveStats() {
    const API_BASE_URL = 'https://api.roftofficial.com';
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/public/stats`);
      if (!response.ok) throw new Error('API unreachable');
      const data = await response.json();
      
      // Update Stats Dashboard with exact database statistics
      updateStatCounter('stat-tuners', data.totalUsers || 0);
      updateStatCounter('stat-vehicles', data.totalVehicles || 0);
      updateStatCounter('stat-meets', data.totalMeets || 0);
      updateStatCounter('stat-likes', data.totalLikes || 0);

      // Update Hall of Fame display (handles any number of verified vehicles, including 0)
      updateHofDisplay(Array.isArray(data.topVehicles) ? data.topVehicles : null);

      // Update federal states active profiles dynamically from database
      if (data.usersByState) {
        updateMapProfiles(data.usersByState);
      }
    } catch (err) {
      updateHofDisplay(null);
      console.warn("Live stats load failed, using fallbacks:", err);
    }
  }

  function updateStatCounter(elementId, targetValue) {
    const el = document.getElementById(elementId);
    if (!el || typeof targetValue !== 'number') return;
    
    if (targetValue === 0) {
      el.textContent = '0';
      return;
    }
    
    let displayVal = targetValue;
    let suffix = '+';
    
    // Dynamically format values over 1000
    if (targetValue >= 1000) {
      displayVal = (targetValue / 1000).toFixed(1);
      if (displayVal.endsWith('.0')) displayVal = Math.floor(targetValue / 1000);
      suffix = 'k+';
    }

    // Smooth count-up animation
    let current = 0;
    const duration = 1000; // ms
    const stepTime = 30;
    const steps = duration / stepTime;
    const increment = parseFloat(displayVal) / steps;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= parseFloat(displayVal)) {
        clearInterval(timer);
        el.textContent = displayVal + suffix;
      } else {
        el.textContent = (targetValue >= 1000 && !Number.isInteger(current) ? current.toFixed(1) : Math.floor(current)) + suffix;
      }
    }, stepTime);
  }

  function updateHofDisplay(vehicles) {
    const showroomTitle = document.querySelector('.showroom-vehicle-title');
    const showroomOwner = document.querySelector('.showroom-vehicle-owner');
    const showroomLikes = document.querySelector('.spec-box:nth-child(2) .spec-value');
    const showroomPower = document.querySelector('.spec-box:nth-child(1) .spec-value');
    const showroomImgWrap = document.querySelector('.showroom-img-wrap');
    const showroomRank = document.querySelector('.spec-box:nth-child(3) .spec-value');
    const hofRankingContainer = document.querySelector('.hof-ranking');
    const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
    const vehicleName = vehicle => [vehicle.brand, vehicle.model].filter(value => typeof value === 'string' && value.trim()).join(' ').trim() || 'Fahrzeug';
    const ratingPoints = vehicle => ['number', 'string'].includes(typeof vehicle.likes) && String(vehicle.likes).trim() && Number.isSafeInteger(Number(vehicle.likes)) && Number(vehicle.likes) >= 0 ? formatNumber(Number(vehicle.likes)) : '—';
    const imagePlaceholder = '<div class="showroom-empty-placeholder" role="img" aria-label="Kein Fahrzeugbild verfügbar" style="display:flex;align-items:center;justify-content:center;height:260px;color:var(--color-text-muted)">Kein Fahrzeugbild verfügbar</div>';

    if (!Array.isArray(vehicles) || vehicles.some(vehicle => !vehicle || typeof vehicle !== 'object' || Array.isArray(vehicle))) {
      const loading = vehicles === undefined;
      const message = loading ? 'Fahrzeuge werden geladen…' : 'Fahrzeuge konnten nicht geladen werden. Bitte lade die Seite erneut.';
      if (showroomTitle) showroomTitle.textContent = message;
      if (showroomOwner) showroomOwner.textContent = '';
      if (showroomLikes) showroomLikes.textContent = '—';
      if (showroomPower) showroomPower.textContent = '- PS';
      if (showroomRank) showroomRank.textContent = '—';
      if (showroomImgWrap) showroomImgWrap.innerHTML = imagePlaceholder;
      if (hofRankingContainer) hofRankingContainer.innerHTML = `<p role="${loading ? 'status' : 'alert'}">${message}</p>`;
      return;
    }

    if (!vehicles || vehicles.length === 0) {
      if (showroomTitle) showroomTitle.textContent = "Noch kein öffentlicher Build";
      if (showroomOwner) showroomOwner.textContent = "In dieser öffentlichen Auswahl ist noch kein Fahrzeug verfügbar.";
      if (showroomLikes) showroomLikes.textContent = "0";
      if (showroomPower) showroomPower.textContent = "- PS";
      if (showroomRank) showroomRank.textContent = '—';
      if (showroomImgWrap) {
        showroomImgWrap.innerHTML = `
          <div class="showroom-empty-placeholder" style="display:flex; flex-direction:column; align-items:center; justify-content:center; height: 260px; background: rgba(255,255,255,0.02); border: 2px dashed rgba(255,255,255,0.1); border-radius: 12px; color: rgba(255,255,255,0.4); text-align: center; padding: 20px;">
            <span style="font-size: 40px; margin-bottom: 12px; filter: drop-shadow(0 0 10px rgba(0, 240, 255, 0.3));">🚗</span>
            <span style="font-size: 14px; font-weight: 700; color: #fff; text-transform: uppercase; letter-spacing: 1px;">Öffentliche Auswahl ist leer</span>
            <span style="font-size: 11px; margin-top: 6px; color: var(--color-text-muted);">Verifizierte, öffentlich sichtbare Builds können hier erscheinen.</span>
          </div>
        `;
      }
    } else {
      const topVehicle = vehicles[0];
      if (showroomTitle) showroomTitle.textContent = vehicleName(topVehicle);
      if (showroomOwner) showroomOwner.textContent = 'Öffentlich sichtbarer, verifizierter Build';
      if (showroomLikes) showroomLikes.textContent = ratingPoints(topVehicle);
      if (showroomPower) showroomPower.textContent = ['number', 'string'].includes(typeof topVehicle.hp) && Number.isFinite(Number(topVehicle.hp)) && Number(topVehicle.hp) > 0 ? `${Number(topVehicle.hp)} PS` : '- PS';
      if (showroomRank) showroomRank.textContent = '#1';
      if (showroomImgWrap) {
        let image = '';
        try {
          if (typeof topVehicle.cover_image_url === 'string' && topVehicle.cover_image_url.trim()) {
            const url = new URL(topVehicle.cover_image_url, 'https://api.roftofficial.com');
            if (url.protocol === 'https:' && !url.username && !url.password) image = url.href;
          }
        } catch { /* A missing or invalid public image keeps the vehicle information usable. */ }
        showroomImgWrap.innerHTML = `${image ? `<img src="${escapeHtml(image)}" class="showroom-vehicle-img" alt="${escapeHtml(vehicleName(topVehicle))}">` : imagePlaceholder}<div class="showroom-vehicle-badge">HALL OF FAME</div>`;
        const img = showroomImgWrap.querySelector('img');
        img?.addEventListener('error', () => {
          if (showroomImgWrap.querySelector('img') === img) showroomImgWrap.innerHTML = imagePlaceholder;
        }, { once: true });
      }
    }

    // Update HOF rankings list dynamically (always show 3 spots)
    if (hofRankingContainer) {
      let rankingHtml = '';
      for (let idx = 0; idx < 3; idx++) {
        const medalClass = idx === 0 ? 'rank-gold' : (idx === 1 ? 'rank-silver' : 'rank-bronze');
        const vehicle = vehicles && vehicles[idx];
        if (vehicle) {
          rankingHtml += `
            <!-- Rank ${idx + 1} -->
            <div class="rank-item">
              <div class="rank-left">
                <span class="rank-number ${medalClass}">${idx + 1}</span>
                <div>
                  <span class="rank-vehicle">${escapeHtml(vehicleName(vehicle))}</span>
                  <p class="rank-owner">Öffentlicher Build</p>
                </div>
              </div>
              <span class="rank-votes">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                ${ratingPoints(vehicle)}
              </span>
            </div>
          `;
        } else {
          rankingHtml += `
            <!-- Rank ${idx + 1} (Empty slot) -->
            <div class="rank-item" style="opacity: 0.5; border-style: dashed;">
              <div class="rank-left">
                <span class="rank-number" style="background: rgba(255,255,255,0.05); color: var(--color-text-muted);">${idx + 1}</span>
                <div>
                  <span class="rank-vehicle" style="color: var(--color-text-muted); font-style: italic;">Noch nicht belegt</span>
                  <p class="rank-owner">@-</p>
                </div>
              </div>
              <span class="rank-votes" style="color: var(--color-text-muted);">
                <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24" style="opacity: 0.3;"><path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"></path></svg>
                0
              </span>
            </div>
          `;
        }
      }
      hofRankingContainer.innerHTML = rankingHtml;
    }
  }

  function formatNumber(num) {
    if (num >= 1000) {
      const formatted = (num / 1000).toFixed(1);
      return formatted.endsWith('.0') ? Math.floor(num / 1000) + 'k' : formatted + 'k';
    }
    return num.toString();
  }

  function updateMapProfiles(usersByState) {
    const mapStates = document.querySelectorAll('.map-state');
    mapStates.forEach(state => {
      const name = state.getAttribute('data-name');
      const count = usersByState[name] || 0;
      
      let formattedCount = count.toString();
      if (count >= 1000) {
        const kValue = (count / 1000).toFixed(1);
        formattedCount = kValue.endsWith('.0') ? Math.floor(count / 1000) + 'k' : kValue + 'k';
      }
      
      state.setAttribute('data-profiles', formattedCount);
    });
  }

  // ==========================================================================
  // 7. Interactive Phone Preview Logic (Status Bar Clock & Inertia Drag-to-Scroll)
  // ==========================================================================
  function initLiveClock() {
    const clockEl = document.getElementById('phone-status-time');
    if (!clockEl) return;
    function updateClock() {
      const now = new Date();
      const hh = String(now.getHours()).padStart(2, '0');
      const mm = String(now.getMinutes()).padStart(2, '0');
      clockEl.textContent = `${hh}:${mm}`;
    }
    updateClock();
    setInterval(updateClock, 1000);
  }

  function initDragToScroll() {
    const viewport = document.getElementById('phone-home-viewport');
    const hint = document.getElementById('phone-scroll-hint');
    if (!viewport) return;

    let isDown = false;
    let startY;
    let scrollTop;
    
    // Velocity tracking variables
    let velY = 0;
    let lastY = 0;
    let lastTime = 0;
    let animationId = null;

    // Deceleration / friction multiplier (0.95 is smooth)
    const friction = 0.95;

    function stopInertia() {
      if (animationId) {
        cancelAnimationFrame(animationId);
        animationId = null;
      }
    }

    function dismissHint() {
      if (hint && !hint.classList.contains('fade-out')) {
        hint.classList.add('fade-out');
        setTimeout(() => hint.remove(), 400);
      }
    }

    viewport.addEventListener('mousedown', (e) => {
      isDown = true;
      stopInertia();
      dismissHint();
      startY = e.pageY - viewport.offsetTop;
      scrollTop = viewport.scrollTop;
      lastY = e.pageY;
      lastTime = Date.now();
      velY = 0;
    });

    viewport.addEventListener('mouseleave', () => {
      if (isDown) {
        isDown = false;
        applyInertia();
      }
    });

    viewport.addEventListener('mouseup', () => {
      if (isDown) {
        isDown = false;
        applyInertia();
      }
    });

    viewport.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      
      const y = e.pageY - viewport.offsetTop;
      const walk = (y - startY) * 1.5; // Drag speed multiplier
      viewport.scrollTop = scrollTop - walk;

      // Calculate dragging velocity for inertia
      const now = Date.now();
      const elapsed = now - lastTime;
      if (elapsed > 0) {
        const deltaY = e.pageY - lastY;
        velY = (deltaY / elapsed) * 16.6; // pixels per frame (60fps scale)
        lastY = e.pageY;
        lastTime = now;
      }
    });

    function applyInertia() {
      if (Math.abs(velY) < 0.5) return;
      
      function step() {
        viewport.scrollTop -= velY;
        velY *= friction;

        if (Math.abs(velY) > 0.1) {
          animationId = requestAnimationFrame(step);
        } else {
          animationId = null;
        }
      }
      animationId = requestAnimationFrame(step);
    }

    // Hide hint when using wheel/touch natively
    viewport.addEventListener('scroll', () => {
      dismissHint();
    });
    
    // Support basic touch start events for mobile devices to hide the hint
    viewport.addEventListener('touchstart', () => {
      dismissHint();
    }, { passive: true });
  }

  // Initialize Clock & Drag mechanics
  initLiveClock();
  initDragToScroll();

  // Load stats once DOM is ready
  updateHofDisplay();
  loadLiveStats();
});
